// One job card on the Jobs tab: a <details> whose summary is the header and whose body is the tracking form.
// Variations and stages nest inside their parent's card as smaller cards.
import type { FieldKey, FieldKind, Item } from '@shared/index';
import {
  baseQuote, craneFlag, CRANE, daysUntil, DRAWINGS, fieldPatch, fmtDate, fmtStamp, holdPatch, isDone, money,
  STATUS_LABEL, STATUSES, WAITING,
} from '@shared/index';
import { activePms, stagesOfJob, store, today, ui } from '../store';
import { addStage, removeStage, save } from '../actions';
import { el, guard, twoTap } from '../ui';
import { jobTasks } from './tasks';

function step(label: string, cls: string): HTMLElement {
  const s = el('div', 'step ' + cls);
  s.append(el('i'), el('span', undefined, label));
  return s;
}

/** A labelled input for one tracking field. Every change goes through the shared side-effect rules. */
export function field(j: Item, key: FieldKey, label: string, kind: FieldKind, options?: readonly (readonly [string, string])[]): HTMLElement {
  const f = el('div', 'field');
  const id = 'f-' + j.quote + '-' + key;
  const l = el('label', undefined, label);
  l.htmlFor = id;
  const cur = (j as unknown as Record<string, unknown>)[key];
  let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
  if (kind === 'select') {
    const sel = el('select');
    (options || []).forEach(([v, t]) => sel.append(new Option(t, v)));
    sel.value = String(cur || (options || [])[0]?.[0] || '');
    input = sel;
  } else if (kind === 'textarea') {
    const ta = el('textarea');
    ta.rows = 3;
    ta.placeholder = 'Site access, builder contacts, delays, anything the team should know';
    ta.value = cur == null ? '' : String(cur);
    f.classList.add('wide');
    input = ta;
  } else {
    const inp = el('input');
    inp.type = kind;
    if (kind === 'number') { inp.min = '0'; inp.step = '1'; inp.inputMode = 'numeric'; }
    inp.value = cur == null ? '' : String(cur);
    input = inp;
  }
  input.id = id;
  input.disabled = !store.canWrite;
  input.addEventListener('change', () => {
    // a focused select counts as "typing" and would hold back the redraw this change needs
    if (kind === 'select') input.blur();
    const patch = fieldPatch(j, key, input.value, kind, today());
    // A stage's name can't be null in the database; clearing it means "Unnamed stage".
    if (key === 'name' && patch.name == null) patch.name = '';
    save(j.quote, patch);
  });
  f.append(l, input);
  return f;
}

export function drawingsGroup(j: Item): HTMLElement {
  const g = el('fieldset', 'drawgrp');
  g.append(el('legend', undefined, 'Drawings'));
  const row = el('div', 'drawrow');
  row.append(
    field(j, 'drawings', 'Stage', 'select', DRAWINGS.map((x) => [x, x] as const)),
    field(j, 'draw_waiting', 'Waiting on', 'select', WAITING),
    field(j, 'drawings_updated', 'Last updated', 'date'),
  );
  g.append(row);
  return g;
}

export function holdToggle(j: Item): HTMLElement {
  const on = j.status === 'hold';
  const wrap = el('div', 'holdrow');
  const b = el('button', 'hold' + (on ? ' on' : ''));
  b.type = 'button';
  b.setAttribute('role', 'switch'); b.setAttribute('aria-checked', String(on));
  b.disabled = !store.canWrite || isDone(j);
  b.append(el('span', 'knob'), el('span', undefined, on ? 'On hold' : 'Put on hold'));
  b.addEventListener('click', () => save(j.quote, holdPatch(j, today())));
  wrap.append(b);
  wrap.append(el('span', 'holdnote', on
    ? 'Since ' + (j.hold_since ? fmtDate(j.hold_since) : 'earlier') + '. Turn off to go back to ' + (STATUS_LABEL[j.prev_status || ''] || 'Not started') + '.'
    : 'Pauses the job and its reminders.'));
  return wrap;
}

/** PM picker in the header. Clicks on it must not open or close the card. */
function pmSelect(j: Item): HTMLSelectElement {
  const pm = el('select', 'pmsel');
  pm.id = 'pm-' + j.quote;
  pm.setAttribute('aria-label', 'Project manager for ' + j.quote);
  pm.disabled = !store.canWrite;
  const names = activePms();
  // a job can belong to a PM who has since been retired; keep them on the list so the value shows
  if (!names.includes(j.pm)) names.push(j.pm);
  names.forEach((n) => pm.append(new Option(n || '—', n)));
  pm.value = j.pm;
  (['click', 'mousedown', 'pointerdown'] as const).forEach((ev) => pm.addEventListener(ev, (e) => e.stopPropagation()));
  pm.addEventListener('click', (e) => e.preventDefault(), true);
  pm.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') e.stopPropagation(); });
  pm.addEventListener('change', () => { pm.blur(); save(j.quote, { pm: pm.value }); });
  return pm;
}

/** Once set, the handover date lives in the header and stays editable there. */
function headerHandover(j: Item): HTMLElement {
  const hd = el('label', 'hdate');
  const inp = el('input');
  inp.type = 'date'; inp.value = j.handed_date || ''; inp.id = 'h-' + j.quote;
  inp.disabled = !store.canWrite;
  inp.setAttribute('aria-label', 'Date handed over for ' + j.quote);
  inp.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); try { inp.showPicker(); } catch { inp.focus(); } });
  inp.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') e.stopPropagation(); });
  inp.addEventListener('change', () => save(j.quote, { handed_date: inp.value || null }));
  hd.addEventListener('click', (e) => { if (e.target !== inp) { e.preventDefault(); e.stopPropagation(); } });
  hd.append(el('span', undefined, 'Date handed over:'), inp);
  return hd;
}

function stagesSwitch(j: Item, count: number): HTMLButtonElement {
  const b = el('button', 'stg' + (j.staged ? ' on' : ''));
  b.type = 'button';
  b.setAttribute('role', 'switch'); b.setAttribute('aria-checked', String(!!j.staged));
  b.disabled = !store.canWrite;
  b.setAttribute('aria-label', 'Split ' + j.quote + ' into stages');
  b.append(el('span', 'knob'), el('span', undefined, j.staged ? 'Staged' + (count ? ' (' + count + ')' : '') : 'Stages'));
  guard(b);
  b.addEventListener('click', (e) => {
    e.preventDefault();
    if (!j.staged) ui.open.add(j.quote);
    save(j.quote, { staged: !j.staged });
  });
  return b;
}

function track(j: Item): HTMLElement {
  const tr = el('div', 'track');
  if (j.isStage) tr.style.gridTemplateColumns = 'repeat(4, minmax(0, 1fr))';
  const drawingsStarted = !!j.drawings && j.drawings !== 'Not started';
  tr.append(
    ...(j.isStage ? [] : [step(j.handed_date ? 'Handed ' + fmtDate(j.handed_date) : 'Not handed', j.handed_date ? 'done' : '')]),
    step(drawingsStarted ? j.drawings + (j.draw_waiting && j.drawings !== 'Approved' ? ' · ' + j.draw_waiting : '') : 'Drawings',
      j.drawings === 'Approved' ? 'done' : drawingsStarted ? 'part' : ''),
    step(j.fab_date ? 'Fab ' + fmtDate(j.fab_date) : 'Not in fab', j.fab_date ? 'done' : ''),
    step(j.install_date ? 'Install ' + fmtDate(j.install_date) : 'No install date', j.install_date ? 'done' : ''),
    step(j.crane === 'Booked' ? 'Crane booked' : j.crane === 'Not needed' ? 'No crane' : 'Crane',
      j.crane === 'Booked' || j.crane === 'Not needed' ? 'done' : craneFlag(j, today()) ? 'flag' : ''),
  );
  return tr;
}

function removeStageButton(j: Item): HTMLButtonElement {
  const rb = el('button', 'rmstage', 'Remove this stage');
  rb.type = 'button';
  twoTap(rb, 'Tap again to remove ' + (j.name || 'this stage'), () => removeStage(j.id || j.quote), 3000);
  return rb;
}

function stagesBox(j: Item, myStages: Item[]): HTMLElement {
  const box = el('div', 'vars stagebox');
  box.append(el('div', 'vhead', 'Stages (' + myStages.length + ')'));
  if (!myStages.length) box.append(el('div', 'note-line', 'No stages yet. Add the first one below, e.g. Ground floor, Level 1, Roof.'));
  myStages.forEach((st) => box.append(card(st, [], true)));
  if (store.canWrite) {
    const ar = el('div', 'addrow');
    const inp = el('input');
    inp.type = 'text'; inp.id = 'stage-' + j.quote; inp.placeholder = 'Stage name, e.g. Level 1 steel';
    inp.setAttribute('aria-label', 'New stage name for ' + j.quote);
    const b = el('button', 'btn', 'Add stage');
    b.type = 'button';
    const go = () => {
      const v = inp.value.trim();
      if (!v) return;
      inp.value = ''; inp.blur();
      addStage(j.quote, v);
    };
    b.addEventListener('click', go);
    inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); go(); } });
    ar.append(inp, b);
    box.append(ar);
  }
  return box;
}

function variationsBox(vars: Item[]): HTMLElement {
  const box = el('div', 'vars');
  box.append(el('div', 'vhead', 'Variations (' + vars.length + ') · ' + money(vars.reduce((t, v) => t + Number(v.quoted || 0), 0)) + ' quoted'));
  vars.forEach((v) => box.append(card(v, [], true)));
  return box;
}

export function card(j: Item, vars: Item[] = [], nested = false, forceOpen = false): HTMLDetailsElement {
  const now = today();
  const d = el('details', 'job' + (nested ? ' nested' : '') + (j.isStage ? ' stage-card' : ''));
  d.open = forceOpen || ui.open.has(j.quote);
  d.addEventListener('toggle', () => { if (d.open) ui.open.add(j.quote); else ui.open.delete(j.quote); });
  const s = el('summary');

  const q = el('div', 'q', j.isStage
    ? 'Stage ' + (j.sort_order || '')
    : j.quote + (j.type === 'Variation' ? (nested ? ' · variation' : ' · variation of ' + baseQuote(j.quote)) : ''));
  if (!j.isStage) q.append(el('span', 'pm', '·'), pmSelect(j));
  if (j.handed_date && !j.isStage) q.append(headerHandover(j));
  const myStages = !j.isStage && !nested ? stagesOfJob(j.quote) : [];
  if (!j.isStage && !nested) q.append(stagesSwitch(j, myStages.length));

  const a = el('div', 'addr', j.isStage ? (j.name || 'Unnamed stage') : j.address);
  const m = el('div', 'meta', j.isStage ? '' : j.builder);

  const r = el('div', 'right');
  const st = j.status || 'not-started';
  r.append(el('span', 'pill s-' + st, STATUS_LABEL[st] || st));
  const leftAmt = Number(j.remaining || 0) + vars.reduce((t, v) => t + Number(v.remaining || 0), 0);
  if (!j.isStage) r.append(el('span', 'money', money(leftAmt) + ' left'));
  if (j.staged && myStages.length) {
    const sc = el('span', 'vcount', myStages.filter(isDone).length + ' of ' + myStages.length + ' stages done');
    if (myStages.some((x) => craneFlag(x, now))) sc.classList.add('hot');
    r.append(sc);
  }
  if (vars.length) {
    const vc = el('span', 'vcount', '+' + vars.length + (vars.length === 1 ? ' variation' : ' variations'));
    if (vars.some((x) => craneFlag(x, now))) vc.classList.add('hot');
    r.append(vc);
  }

  const stagedMain = j.staged && !j.isStage && !nested;
  s.append(q, r, a, m);
  if (!stagedMain) s.append(track(j));
  if (j.notes) s.append(el('div', 'note-line', 'Note: ' + j.notes.split('\n')[0]));
  if (j.completed_date) s.append(el('div', 'done-line', 'Completed ' + fmtDate(j.completed_date)));
  if (craneFlag(j, now)) {
    const n = daysUntil(j.install_date, now)!;
    s.append(el('div', 'flagline', 'Install in ' + n + (n === 1 ? ' day' : ' days') + ' and no crane booked'));
  }
  d.append(s);

  const form = el('div', 'form');
  if (stagedMain) {
    // The stages carry the tracking; the job itself only needs its handover and completion dates.
    if (!j.handed_date) form.append(field(j, 'handed_date', 'Date handed over', 'date'));
    form.append(field(j, 'completed_date', 'Date completed', 'date'));
  } else {
    form.append(holdToggle(j));
    if (j.isStage) form.append(field(j, 'name', 'Stage name', 'text'));
    form.append(
      field(j, 'status', 'Job status', 'select', STATUSES),
      ...(j.handed_date || j.isStage ? [] : [field(j, 'handed_date', 'Date handed over', 'date')]),
      drawingsGroup(j),
      field(j, 'fab_date', 'Handed to fabrication', 'date'),
      field(j, 'install_date', 'Install date', 'date'),
      field(j, 'crane', 'Crane', 'select', CRANE.map((x) => [x, x] as const)),
      field(j, 'days_onsite', 'Days allowed onsite', 'number'),
      field(j, 'completed_date', 'Date completed', 'date'),
      field(j, 'notes', 'Notes', 'textarea'),
    );
  }
  const foot = el('div', 'foot');
  foot.append(
    el('span', undefined, j.isStage ? 'Stage of ' + j.ref : 'Quoted ' + money(j.quoted) + ' · quote date ' + fmtDate(j.quote_date)),
    el('span', undefined, j.updated_at ? 'Last updated ' + fmtStamp(j.updated_at) : 'Not updated yet'),
  );
  if (!stagedMain) form.append(jobTasks(j));
  if (j.isStage && store.canWrite) form.append(removeStageButton(j));
  form.append(foot);
  d.append(form);
  if (stagedMain) d.append(stagesBox(j, myStages));
  if (vars.length) d.append(variationsBox(vars));
  return d;
}
